import React, { useState, useEffect } from 'react';
import {
  RefreshCw,
  Download,
  AlertTriangle,
  CheckCircle,
  Loader,
  Package,
  MapPin,
  TrendingUp,
  FileText,
  X,
  Check,
  Edit2
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import inventoryCountingDashboardService, {
  CountingSummary,
  CountingDetail,
  CountingBinStatus,
  CountingDashboardData
} from '../../../../services/inventoryCountingDashboardService';
import { warehouseService } from '../../../../services/warehouseService';
import { BackButton } from '../../../common/BackButton';
import { CountingListOverview } from './CountingListOverview';
import { BalanceComparisonTab } from './BalanceComparisonTab';

interface CountingDashboardProps {
  onBack: () => void;
  /** Optional initial tab — used for deep-link/launch from BalanceComparison shortcut. */
  initialTab?: 'free-counting' | 'counting-lists' | 'balance-comparison';
}

interface WarehouseOption {
  warehouseID: string;
  warehouseDescr: string;
}

export const CountingDashboard: React.FC<CountingDashboardProps> = ({ onBack, initialTab }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // State
  const [dashboardData, setDashboardData] = useState<CountingDashboardData | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deviationOnly, setDeviationOnly] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'free-counting' | 'counting-lists' | 'balance-comparison'>(
    initialTab ?? 'free-counting'
  );
  const [countingListRefreshKey, setCountingListRefreshKey] = useState(0);

  // Detail/drill-down state
  const [selectedItem, setSelectedItem] = useState<CountingSummary | null>(null);
  const [countingDetails, setCountingDetails] = useState<CountingDetail[]>([]);
  const [binStatus, setBinStatus] = useState<CountingBinStatus[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingLine, setEditingLine] = useState<{ stcTransId: number; quantity: string } | null>(null);

  // Load warehouses on mount
  useEffect(() => {
    loadWarehouses();
  }, []);

  // Load dashboard data when warehouse is selected
  useEffect(() => {
    if (selectedWarehouse) {
      loadDashboardData();
    }
  }, [selectedWarehouse, deviationOnly]);

  const loadWarehouses = async () => {
    try {
      const result = await warehouseService.getWarehouses();
      setWarehouses(result);

      if (result.length > 0 && !selectedWarehouse) {
        setSelectedWarehouse(result[0].warehouseID);
      }
    } catch (error) {
      console.error('Error loading warehouses:', error);
    }
  };

  const loadDashboardData = async () => {
    if (!selectedWarehouse) return;

    try {
      setLoading(true);
      const data = await inventoryCountingDashboardService.getCountingDashboard(
        selectedWarehouse,
        undefined,
        deviationOnly,
        undefined,
        true // freeCountingOnly - exclude counting list lines (LineType=4)
      );
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setCountingListRefreshKey(prev => prev + 1);
    setRefreshing(false);
  };

  const handleItemClick = async (item: CountingSummary) => {
    setSelectedItem(item);
    setDetailLoading(true);

    try {
      // Load details
      const details = await inventoryCountingDashboardService.getCountingDetails(
        selectedWarehouse,
        item.itemNo
      );
      setCountingDetails(details);

      // Load bin status if AW warehouse
      if (dashboardData?.warehouse?.useAWYesNo) {
        const bins = await inventoryCountingDashboardService.getCountingBinStatus(
          selectedWarehouse,
          item.itemNo
        );
        setBinStatus(bins);
      }
    } catch (error) {
      console.error('Error loading item details:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedItem(null);
    setCountingDetails([]);
    setBinStatus([]);
    setEditingLine(null);
  };

  const handleToggleItemSelection = (itemNo: string) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(itemNo)) {
      newSelection.delete(itemNo);
    } else {
      newSelection.add(itemNo);
    }
    setSelectedItems(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === dashboardData?.summary.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(dashboardData?.summary.map(s => s.itemNo) || []));
    }
  };

  const handleExport = async () => {
    if (selectedItems.size === 0) {
      alert(t('warehouse.modules.inventoryDashboard.messages.selectAtLeastOneItem'));
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (!confirm(t('warehouse.modules.inventoryDashboard.confirmations.exportItems', { '0': selectedItems.size }))) {
      return;
    }

    try {
      setIsExporting(true);
      const result = await inventoryCountingDashboardService.exportCounting(
        selectedWarehouse,
        Array.from(selectedItems),
        user?.operatorName
      );

      if (result.success) {
        alert(t('warehouse.modules.inventoryDashboard.messages.exportSuccess', { '0': result.itemsExported, '1': result.transactionsExported }) +
              (result.itemBinsUpdated ? t('warehouse.modules.inventoryDashboard.messages.itemBinsUpdated') : ''));

        // Clear selection and refresh
        setSelectedItems(new Set());
        await loadDashboardData();
      } else {
        alert(t('warehouse.modules.inventoryDashboard.messages.exportError', { '0': result.errorMessage || '' }));
      }
    } catch (error) {
      console.error('Error exporting:', error);
      alert(t('warehouse.modules.inventoryDashboard.messages.exportErrorGeneral', { '0': error instanceof Error ? error.message : t('warehouse.modules.inventoryDashboard.messages.unknownError') }));
    } finally {
      setIsExporting(false);
    }
  };

  const handleUpdateCorrection = async (detail: CountingDetail, newQuantity: number) => {
    try {
      await inventoryCountingDashboardService.updateCountingCorrection(
        detail.batchID,
        detail.lineID,
        newQuantity,
        user?.operatorName
      );

      // Refresh details
      if (selectedItem) {
        const details = await inventoryCountingDashboardService.getCountingDetails(
          selectedWarehouse,
          selectedItem.itemNo
        );
        setCountingDetails(details);

        // Refresh summary to update totals
        await loadDashboardData();
      }

      setEditingLine(null);
    } catch (error) {
      console.error('Error updating correction:', error);
      alert(t('warehouse.modules.inventoryDashboard.messages.updateCorrectionError'));
    }
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-lg">{t('warehouse.modules.inventoryDashboard.status.loadingDashboard')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <BackButton onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" iconSize={20} label="" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('warehouse.modules.inventoryDashboard.title')}</h1>
                <p className="text-sm text-gray-600">{t('warehouse.modules.inventoryDashboard.description')}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Warehouse selector */}
              <select
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {warehouses.map((wh) => (
                  <option key={wh.warehouseID} value={wh.warehouseID}>
                    {wh.warehouseDescr}
                  </option>
                ))}
              </select>

              {/* Deviation filter (free counting only) */}
              {activeTab === 'free-counting' && (
              <label className="flex items-center space-x-2 px-4 py-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200">
                <input
                  type="checkbox"
                  checked={deviationOnly}
                  onChange={(e) => setDeviationOnly(e.target.checked)}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium">{t('warehouse.modules.inventoryDashboard.filters.deviationsOnly')}</span>
              </label>
              )}

              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{t('common.buttons.refresh')}</span>
              </button>

              {activeTab === 'free-counting' && selectedItems.size > 0 && (
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  <span>{t('warehouse.modules.inventoryDashboard.buttons.export', { '0': selectedItems.size })}</span>
                  {isExporting && <Loader className="w-4 h-4 animate-spin ml-2" />}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex border-t border-gray-200">
          <button
            onClick={() => setActiveTab('free-counting')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'free-counting'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('warehouse.modules.inventoryDashboard.title')}
          </button>
          <button
            onClick={() => setActiveTab('counting-lists')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'counting-lists'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            Tellelister
          </button>
          <button
            onClick={() => setActiveTab('balance-comparison')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'balance-comparison'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('warehouse.modules.balanceComparison.tabTitle', {}, 'ERP-Saldo Sammenligning')}
          </button>
        </div>

        {/* Statistics bar (free counting only) */}
        {activeTab === 'free-counting' && dashboardData && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <div className="grid grid-cols-4 gap-4">
              <div className="flex items-center space-x-3">
                <Package className="w-5 h-5 text-blue-600" />
                <div>
                  <div className="text-sm text-gray-600">{t('warehouse.modules.inventoryDashboard.stats.totalItems')}</div>
                  <div className="text-lg font-semibold">{dashboardData.totalItems}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                <div>
                  <div className="text-sm text-gray-600">{t('warehouse.modules.inventoryDashboard.stats.itemsWithDeviation')}</div>
                  <div className="text-lg font-semibold text-orange-600">
                    {dashboardData.itemsWithDeviation}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <TrendingUp className="w-5 h-5 text-red-600" />
                <div>
                  <div className="text-sm text-gray-600">{t('warehouse.modules.inventoryDashboard.stats.totalDeviationValue')}</div>
                  <div className="text-lg font-semibold text-red-600">
                    {Math.abs(dashboardData.totalDeviationValue).toFixed(2)} {t('warehouse.general.currency')}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <div className="text-sm text-gray-600">{t('warehouse.modules.inventoryDashboard.stats.correctedItems')}</div>
                  <div className="text-lg font-semibold text-green-600">
                    {dashboardData.correctedItems}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main content - Free counting tab */}
      {activeTab === 'free-counting' && (
      <div className="flex-1 overflow-auto p-6">
        {!selectedItem ? (
          /* Summary Grid */
          <div className="bg-white rounded-lg shadow">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t('warehouse.modules.inventoryDashboard.tables.countingOverviewPerItem')}</h2>
              {dashboardData && dashboardData.summary.length > 0 && (
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {selectedItems.size === dashboardData.summary.length ? t('common.buttons.selectNone') : t('common.buttons.selectAll')}
                </button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={dashboardData ? (selectedItems.size === dashboardData.summary.length && dashboardData.summary.length > 0) : false}
                        onChange={handleSelectAll}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouse.general.itemNumber')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.fields.description')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouse.general.location')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouse.modules.inventoryDashboard.tableHeaders.counted')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouse.modules.inventoryDashboard.tableHeaders.expected')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouse.modules.inventoryDashboard.tableHeaders.deviation')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouse.modules.inventoryDashboard.tableHeaders.value')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.fields.status')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouse.modules.inventoryDashboard.tableHeaders.countTransactions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardData?.summary.map((item) => (
                    <tr
                      key={item.itemNo}
                      className={`hover:bg-gray-50 cursor-pointer ${item.hasDeviation ? 'bg-orange-50' : ''}`}
                      onClick={() => handleItemClick(item)}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.itemNo)}
                          onChange={() => handleToggleItemSelection(item.itemNo)}
                          className="rounded text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {item.itemNo}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {item.description1}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.stdLocationName || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">
                        {item.quantityCounted}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        {item.quantityExpected}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${
                        item.hasDeviation ? 'text-orange-600' : 'text-green-600'
                      }`}>
                        {item.deviationQuantity !== 0 ? item.deviationQuantity.toFixed(2) : '-'}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${
                        item.hasDeviation ? 'text-red-600' : ''
                      }`}>
                        {item.deviationValue !== 0 ? `${Math.abs(item.deviationValue).toFixed(2)} ${t('warehouse.general.currency')}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.isCorrected ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {t('warehouse.modules.inventoryDashboard.status.corrected')}
                          </span>
                        ) : item.hasDeviation ? (
                          <AlertTriangle className="w-5 h-5 text-orange-500 mx-auto" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">
                        {item.transactionCount}
                      </td>
                    </tr>
                  ))}
                  {(!dashboardData || dashboardData.summary.length === 0) && (
                    <tr>
                      <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                        {t('warehouse.modules.inventoryDashboard.messages.noCountsFound')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Detail View */
          <div className="space-y-4">
            {/* Back to summary */}
            <BackButton
              onClick={handleCloseDetail}
              className="text-blue-600 hover:text-blue-700 font-medium"
              iconSize={16}
              label={t('warehouse.modules.inventoryDashboard.navigation.backToOverview')}
            />

            {/* Item header */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedItem.itemNo}</h2>
                  <p className="text-gray-600 mt-1">{selectedItem.description1}</p>
                  <div className="flex items-center space-x-4 mt-4">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {t('warehouse.general.location')}: {selectedItem.stdLocationName || 'Ingen'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Package className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {t('warehouse.modules.inventoryDashboard.tableHeaders.counted')}: {selectedItem.quantityCounted} | Forventet: {selectedItem.quantityExpected}
                      </span>
                    </div>
                    {selectedItem.hasDeviation && (
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                        <span className="text-sm font-medium text-orange-600">
                          {t('warehouse.modules.inventoryDashboard.tableHeaders.deviation')}: {selectedItem.deviationQuantity.toFixed(2)} ({Math.abs(selectedItem.deviationValue).toFixed(2)} kr)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Details grid */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold">{t('warehouse.modules.inventoryDashboard.sections.countDetails')}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.general.location')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Serienr.
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Batch
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.general.operator')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.general.timestamp')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.inventoryDashboard.tableHeaders.expectedQuantity')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.inventoryDashboard.tableHeaders.countedQuantity')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.inventoryDashboard.tableHeaders.correctedQuantity')}
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.inventoryDashboard.tableHeaders.action')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {detailLoading ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center">
                          <Loader className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
                        </td>
                      </tr>
                    ) : countingDetails.length > 0 ? (
                      countingDetails.map((detail) => (
                        <tr key={detail.stcTransId} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {detail.locationName || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {detail.itemSerialNo ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {detail.itemSerialNo}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {detail.itemBatchNo ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                {detail.itemBatchNo}
                                {detail.itemBatchExpDate && (
                                  <span className="ml-1 text-orange-600">
                                    ({new Date(detail.itemBatchExpDate).toLocaleDateString('no-NO')})
                                  </span>
                                )}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {detail.operatorID}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {detail.transDt ? new Date(detail.transDt).toLocaleString('no-NO') : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-600">
                            {detail.quantityExpected ?? '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium">
                            {detail.quantityOriginal || 0}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {editingLine?.stcTransId === detail.stcTransId ? (
                              <div className="flex items-center justify-end space-x-2">
                                <input
                                  type="number"
                                  value={editingLine.quantity}
                                  onChange={(e) => setEditingLine({ ...editingLine, quantity: e.target.value })}
                                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                  step="0.01"
                                />
                                <button
                                  onClick={() => handleUpdateCorrection(detail, parseFloat(editingLine.quantity))}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setEditingLine(null)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <span className={`text-sm font-medium ${
                                detail.quantityCorrected !== null && detail.quantityCorrected !== undefined
                                  ? 'text-blue-600'
                                  : ''
                              }`}>
                                {detail.quantityCorrected !== null && detail.quantityCorrected !== undefined
                                  ? detail.quantityCorrected
                                  : '-'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {!editingLine && (
                              <button
                                onClick={() => setEditingLine({
                                  stcTransId: detail.stcTransId,
                                  quantity: String(detail.quantityCorrected ?? detail.quantityOriginal ?? 0)
                                })}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                          {t('warehouse.modules.inventoryDashboard.messages.noDetailsFound')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bin status (AW only) */}
            {dashboardData?.warehouse?.useAWYesNo && binStatus.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-lg font-semibold">{t('warehouse.modules.inventoryDashboard.sections.stockStatus')}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('warehouse.general.bin')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Serienr.
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Batch
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('warehouse.general.quantity')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('warehouse.general.reserved')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('warehouse.general.available')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('warehouse.modules.inventoryDashboard.tableHeaders.lastCounted')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('warehouse.modules.inventoryDashboard.tableHeaders.lastMovement')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {binStatus.map((bin, idx) => (
                        <tr key={`${bin.binCode}-${bin.serialNo || idx}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {bin.binCode}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {bin.serialNo ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {bin.serialNo}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {bin.batchNo ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                {bin.batchNo}
                                {bin.expiryDate && (
                                  <span className="ml-1 text-orange-600">
                                    ({new Date(bin.expiryDate).toLocaleDateString('no-NO')})
                                  </span>
                                )}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right">
                            {bin.quantity || 0}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-orange-600">
                            {bin.resQuantity || 0}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-green-600">
                            {bin.availableQuantity || 0}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {bin.lastCountedDate ? new Date(bin.lastCountedDate).toLocaleDateString('no-NO') : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {bin.lastMovementDate ? new Date(bin.lastMovementDate).toLocaleDateString('no-NO') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Main content - Counting lists tab */}
      {activeTab === 'counting-lists' && selectedWarehouse && (
        <div className="flex-1 min-h-0 overflow-hidden">
          <CountingListOverview warehouse={selectedWarehouse} refreshKey={countingListRefreshKey} />
        </div>
      )}

      {/* Main content - Balance comparison tab (WMS vs ERP) */}
      {activeTab === 'balance-comparison' && selectedWarehouse && (
        <div className="flex-1 min-h-0 overflow-auto p-6">
          <BalanceComparisonTab warehouseID={selectedWarehouse} />
        </div>
      )}
    </div>
  );
};
